import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import KnowledgeBaseArticle, {
	CreateArticleRequest,
	UpdateArticleRequest,
	ArticleFeedbackRequest,
} from "../models/KnowledgeBaseArticle";
import { ApiErrorResponse } from "../models/ApiResponse";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

/**
 * Hook to fetch knowledge base articles by tags.
 * @param tags Array of tags to filter by. If empty, returns all articles.
 * @param enabled Whether the query should run automatically
 */
export function useKnowledgeBaseArticles(tags?: string[], enabled: boolean = true) {
	const { data, ...rest } = useQuery(
		["knowledge-base-articles", tags?.join(",")],
		() => {
			const queryParams = tags && tags.length > 0 ? { tags: tags.join(",") } : undefined;
			return apiStore
				.get<KnowledgeBaseArticle[]>({
					path: "/knowledge-base-articles",
					isAuthRequired: true,
					query: queryParams,
				})
				.then((d) => d.data);
		},
		{ enabled }
	);

	return {
		articles: data || [],
		...rest,
	};
}

/**
 * Hook to fetch a single knowledge base article by id.
 * @param articleId The article id
 * @param enabled Whether the query should run automatically
 */
export function useKnowledgeBaseArticle(articleId?: string, enabled: boolean = true) {
	const { data, ...rest } = useQuery(
		["knowledge-base-article", articleId],
		() =>
			apiStore
				.get<KnowledgeBaseArticle>({
					path: `/knowledge-base-article/${articleId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ enabled: !!articleId && enabled }
	);

	return {
		article: data,
		...rest,
	};
}

/**
 * Hook to create a new knowledge base article.
 * Requires knowledge_base_editor role.
 */
export function useCreateArticle() {
	const queryClient = useQueryClient();

	return useMutation<KnowledgeBaseArticle, ApiErrorResponse, CreateArticleRequest>(
		(request) =>
			apiStore
				.post({
					path: "/knowledge-base-article",
					body: request,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["knowledge-base-articles"]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

/**
 * Hook to update an existing knowledge base article.
 * Requires knowledge_base_editor role.
 */
export function useUpdateArticle() {
	const queryClient = useQueryClient();

	return useMutation<
		KnowledgeBaseArticle,
		ApiErrorResponse,
		{ articleId: string; update: UpdateArticleRequest }
	>(
		(params) =>
			apiStore
				.put({
					path: `/knowledge-base-article/${params.articleId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (_, params) => {
				toast.successGeneric();
				queryClient.invalidateQueries(["knowledge-base-article", params.articleId]);
				queryClient.invalidateQueries(["knowledge-base-articles"]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

/**
 * Hook to delete a knowledge base article.
 * Requires knowledge_base_editor role.
 */
export function useDeleteArticle() {
	const queryClient = useQueryClient();

	return useMutation<void, ApiErrorResponse, string>(
		(articleId) =>
			apiStore
				.delete({
					path: `/knowledge-base-article/${articleId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				toast.successGeneric();
				queryClient.invalidateQueries(["knowledge-base-articles"]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

/**
 * Hook to record feedback (thumbs up/down) for an article.
 */
export function useRecordFeedback() {
	const queryClient = useQueryClient();

	return useMutation<
		KnowledgeBaseArticle,
		ApiErrorResponse,
		{ articleId: string; isPositive: boolean }
	>(
		(params) =>
			apiStore
				.post({
					path: `/knowledge-base-article/${params.articleId}/feedback`,
					body: { isPositive: params.isPositive },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (_, params) => {
				// Update the article in cache without showing toast
				queryClient.invalidateQueries(["knowledge-base-article", params.articleId]);
				queryClient.invalidateQueries(["knowledge-base-articles"]);
			},
			// Don't show error toast for feedback - it's not critical
		}
	);
}
